import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Collapse,
  Button,
  Badge,
  Tooltip,
  Divider,
  Switch
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Class as ClassIcon,
  CheckCircle as CheckCircleIcon,
  AttachMoney as AttachMoneyIcon,
  LibraryBooks as LibraryBooksIcon,
  DirectionsBus as DirectionsBusIcon,
  Assignment as AssignmentIcon,
  Assessment as AssessmentIcon,
  Event as EventIcon,
  AccountCircle,
  Logout,
  ExpandLess,
  ExpandMore,
  Settings as SettingsIcon,
  Book as BookIcon,
  EventNote as EventNoteIcon,
  Description as DescriptionIcon,
  CalendarMonth as CalendarMonthIcon,
  Security as SecurityIcon,
  Hotel as HotelIcon,
  Inventory as InventoryIcon,
  Payment as PaymentIcon,
  Email as EmailIcon,
  Autorenew as AutorenewIcon,
  Notifications as NotificationsIcon,
  Badge as BadgeIcon,
  Schedule as ScheduleIcon,
  SwapHoriz as SwapHorizIcon,
  Sms as SmsIcon,
  Receipt as ReceiptIcon,
  AccountBalance as AccountBalanceIcon,
  Analytics as AnalyticsIcon,
  MonitorHeart as MonitorHeartIcon,
  PhoneAndroid as PhoneAndroidIcon,
  HelpCenter as HelpCenterIcon,
  Tune as TuneIcon,
  Search as SearchIcon,
  Language as LanguageIcon,
  FormatSize as FormatSizeIcon,
  LiveHelp as LiveHelpIcon,
  ChevronLeft as ChevronLeftIcon,
  School as SchoolIcon,
  AccountBalanceWallet as WalletIcon,
  ContentPaste as ContentPasteIcon,
  DataUsage as DataUsageIcon,
  VerifiedUser as VerifiedUserIcon,
  UploadFile as UploadFileIcon,
  Build as BuildIcon,
  PersonAdd as PersonAddIcon,
  Palette as PaletteIcon,
  Apps as AppsIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const drawerWidth = 220;

// Menu items organized by ePrashasan-style sections
// Each item has a 'permission' key that maps to a module in the RBAC permissions table
const sidebarSections = [
  {
    title: 'Quick Access',
    defaultOpen: true,
    items: [
      { text: 'Dashboard', icon: <DashboardIcon />, path: '/', permission: 'dashboard' },
      { text: 'Students', icon: <PeopleIcon />, path: '/students', permission: 'students' },
      { text: 'Attendance', icon: <CheckCircleIcon />, path: '/attendance', permission: 'attendance' },
      { text: 'Fees', icon: <AttachMoneyIcon />, path: '/fees', permission: 'fees' },
      { text: 'Classes', icon: <ClassIcon />, path: '/classes', permission: 'classes' },
    ]
  },
  {
    title: 'Communication',
    defaultOpen: false,
    items: [
      { text: 'Letters', icon: <EmailIcon />, path: '/letters', permission: 'communication' },
      { text: 'Notices', icon: <NotificationsIcon />, path: '/communication/notices', permission: 'communication' },
      { text: 'SMS', icon: <SmsIcon />, path: '/sms', permission: 'communication' },
    ]
  },
  {
    title: 'Administration',
    defaultOpen: true,
    items: [
      { text: 'Search Outward Register', icon: <SearchIcon />, path: '/inward-outward', permission: 'inward_outward' },
      { text: 'Admissions', icon: <PersonAddIcon />, path: '/admission/online', permission: 'students' },
      { text: 'Admission Form', icon: <DescriptionIcon />, path: '/admission/form', permission: 'students' },
      { text: 'Certificates', icon: <DescriptionIcon />, path: '/certificates', permission: 'certificates' },
      { text: 'Print IdCard', icon: <BadgeIcon />, path: '/idcards', permission: 'idcards' },
      { text: 'Print Staff Id Card', icon: <BadgeIcon />, path: '/staff/idcards', permission: 'idcards' },
      { text: 'Timetable Settings', icon: <SettingsIcon />, path: '/timetable/settings', permission: 'timetable' },
      { text: 'Class Timetables', icon: <ScheduleIcon />, path: '/timetable', permission: 'timetable' },
      { text: 'Inward Outward Management', icon: <SwapHorizIcon />, path: '/inward-outward', permission: 'inward_outward' },
      { text: 'Enquiry Dashboard', icon: <HelpCenterIcon />, path: '/enquiry', permission: 'enquiry' },
      { text: 'Transport', icon: <DirectionsBusIcon />, path: '/transport', permission: 'transport' },
      { text: 'Hostel Management', icon: <HotelIcon />, path: '/hostel', permission: 'hostel' },
      { text: 'Go to Library', icon: <LibraryBooksIcon />, path: '/library', permission: 'library' },
      { text: 'Upload Letterheads', icon: <UploadFileIcon />, path: '/settings/letterheads', permission: 'settings' },
      { text: 'Data Verification', icon: <DataUsageIcon />, path: '/data-verification', permission: 'data_verification' },
      { text: 'General Register', icon: <ContentPasteIcon />, path: '/general-register', permission: 'general_register' },
      { text: 'Visitor Management', icon: <PeopleIcon />, path: '/visitors', permission: 'visitors' },
    ]
  },
  {
    title: 'Finance',
    defaultOpen: true,
    items: [
      { text: 'Accounts', icon: <AccountBalanceIcon />, path: '/accounts', permission: 'accounts' },
      { text: 'Fee Management', icon: <AttachMoneyIcon />, path: '/fees', permission: 'fees' },
      { text: 'Prospect Fee Management', icon: <WalletIcon />, path: '/fees/prospect', permission: 'fees' },
      { text: 'Send Pending Fee Reminder', icon: <NotificationsIcon />, path: '/fees/reminder', permission: 'fees' },
      { text: 'Paid Fee Report', icon: <AssessmentIcon />, path: '/reports/fees', permission: 'reports' },
      { text: 'Payroll', icon: <PaymentIcon />, path: '/payroll', permission: 'payroll' },
      { text: 'Scholarship', icon: <SchoolIcon />, path: '/scholarship', permission: 'scholarship' },
    ]
  },
  {
    title: 'Exams & Results',
    defaultOpen: false,
    items: [
      { text: 'Exam Setup', icon: <SettingsIcon />, path: '/exams/setup', permission: 'exams' },
      { text: 'Subject Allocation', icon: <BookIcon />, path: '/exams/subject-allocation', permission: 'exams' },
      { text: 'Marks Entry', icon: <AssignmentIcon />, path: '/exams/marks-entry', permission: 'exams' },
      { text: 'Monitoring', icon: <MonitorHeartIcon />, path: '/exams/monitoring', permission: 'exams' },
      { text: 'Student Report', icon: <AssessmentIcon />, path: '/exams/student-report', permission: 'exams' },
      { text: 'Exam Management', icon: <TuneIcon />, path: '/exams/management', permission: 'exams' },
      { text: 'Result Print', icon: <DescriptionIcon />, path: '/exams/result-print', permission: 'exams' },
    ]
  },
  {
    title: 'Staff',
    defaultOpen: false,
    items: [
      { text: 'Staff List', icon: <PeopleIcon />, path: '/staff', permission: 'staff' },
      { text: 'Approvals', icon: <AssignmentIcon />, path: '/staff/approval', permission: 'staff' },
      { text: 'Leave Mgmt', icon: <EventNoteIcon />, path: '/staff/leave', permission: 'staff' },
      { text: 'Staff Attendance', icon: <CheckCircleIcon />, path: '/staff/attendance', permission: 'staff' },
    ]
  },
  {
    title: 'Reports',
    defaultOpen: false,
    items: [
      { text: 'All Reports', icon: <AssessmentIcon />, path: '/reports', permission: 'reports' },
      { text: 'Student Summary', icon: <PeopleIcon />, path: '/student-summary', permission: 'reports' },
      { text: 'Student Analytics', icon: <AnalyticsIcon />, path: '/analytics/students', permission: 'reports' },
      { text: 'Birthday Report', icon: <EventIcon />, path: '/reports/birthdays', permission: 'reports' },
      { text: 'Attendance Calendar', icon: <CalendarMonthIcon />, path: '/attendance/calendar', permission: 'attendance' },
      { text: 'Attendance Monitoring', icon: <MonitorHeartIcon />, path: '/attendance/monitoring', permission: 'attendance' },
      { text: 'Cumulative Report', icon: <ContentPasteIcon />, path: '/reports/cumulative', permission: 'reports' },
      { text: 'Transport Fee Report', icon: <DirectionsBusIcon />, path: '/transport/reports', permission: 'transport' },
    ]
  },
  {
    title: 'Settings',
    defaultOpen: false,
    items: [
      { text: 'General Settings', icon: <SettingsIcon />, path: '/settings', permission: 'settings' },
      { text: 'Master Data', icon: <TuneIcon />, path: '/settings/masters', permission: 'settings' },
      { text: 'Roles & Permissions', icon: <SecurityIcon />, path: '/roles', permission: 'roles' },
      { text: 'Holidays', icon: <EventIcon />, path: '/holidays', permission: 'settings' },
      { text: 'Auto Renewal', icon: <AutorenewIcon />, path: '/students/auto-renewal', permission: 'students' },
      { text: 'Under Maintenance', icon: <BuildIcon />, path: '/settings/maintenance', permission: 'settings' },
      { text: 'App Download Monitor', icon: <PhoneAndroidIcon />, path: '/monitoring/app-downloads', permission: 'settings' },
    ]
  },
];

const Layout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceData, setMaintenanceData] = useState({});
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Initialize open sections based on defaultOpen
  const [openSections, setOpenSections] = useState(() => {
    const initial = {};
    sidebarSections.forEach(section => {
      initial[section.title] = section.defaultOpen;
    });
    return initial;
  });

  // Dynamic Theme & Layout Customization State
  const [themeAnchorEl, setThemeAnchorEl] = useState(null);
  const [activeTheme, setActiveTheme] = useState(() => localStorage.getItem('vpmis-theme') || 'emerald');
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('vpmis-dark-mode') === 'true');
  const [navMode, setNavMode] = useState(() => localStorage.getItem('vpmis-nav-mode') || 'sidebar');
  const [launchpadOpen, setLaunchpadOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [hoveredIndex, setHoveredIndex] = useState(null);

  useEffect(() => {
    document.body.classList.remove('theme-emerald', 'theme-ocean', 'theme-rose', 'theme-cyber');
    document.body.classList.add(`theme-${activeTheme}`);
    localStorage.setItem('vpmis-theme', activeTheme);
  }, [activeTheme]);

  useEffect(() => {
    if (darkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    localStorage.setItem('vpmis-dark-mode', String(darkMode));
  }, [darkMode]);

  useEffect(() => {
    localStorage.setItem('vpmis-nav-mode', navMode);
  }, [navMode]);

  const handleThemeMenuOpen = (event) => {
    setThemeAnchorEl(event.currentTarget);
  };

  const handleThemeMenuClose = () => {
    setThemeAnchorEl(null);
  };

  // RBAC Filter - check if user has view permission for a module
  // MUST be defined before dock items array which filters by permission
  const hasPermission = (moduleName) => {
    if (!user) return false;
    if (user.role === 'super_admin' || user.role === 'admin') return true;
    const perms = user.userRole?.permissions || [];
    return perms.some(p => p.module === moduleName || p.slug === `${moduleName}_view`);
  };

  const toggleSection = (title) => {
    setOpenSections(prev => ({
      ...prev,
      [title]: !prev[title]
    }));
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // macOS Dock Helper Constants & Methods
  const dockNavItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/', permission: 'dashboard' },
    { text: 'Students', icon: <PeopleIcon />, path: '/students', permission: 'students' },
    { text: 'Attendance', icon: <CheckCircleIcon />, path: '/attendance', permission: 'attendance' },
    { text: 'Fees', icon: <AttachMoneyIcon />, path: '/fees', permission: 'fees' },
    { text: 'Classes', icon: <ClassIcon />, path: '/classes', permission: 'classes' },
    { text: 'Exams', icon: <AssessmentIcon />, path: '/exams/management', permission: 'exams' },
    { text: 'Staff', icon: <PeopleIcon />, path: '/staff', permission: 'staff' },
    { text: 'Reports', icon: <AssessmentIcon />, path: '/reports', permission: 'reports' },
  ];

  const visibleDockItems = dockNavItems.filter(item => hasPermission(item.permission));

  const allDockItems = [
    { id: 'launchpad', text: 'Launchpad', icon: <AppsIcon />, isAction: true, onClick: () => setLaunchpadOpen(true) },
    { id: 'divider-1', isDivider: true },
    ...visibleDockItems.map(item => ({ ...item, id: item.path, isLink: true })),
    { id: 'divider-2', isDivider: true },
    { id: 'theme', text: 'Themes', icon: <PaletteIcon />, isAction: true, onClick: handleThemeMenuOpen },
  ];

  let nonDividerCounter = 0;
  const itemsWithIndex = allDockItems.map(item => {
    if (item.isDivider) return item;
    const currentIdx = nonDividerCounter;
    nonDividerCounter++;
    return { ...item, index: currentIdx };
  });

  const getDockItemStyle = (idx) => {
    if (idx === undefined || hoveredIndex === null) return {};
    const distance = Math.abs(idx - hoveredIndex);
    let scale = 1.0;
    let translateY = 0;
    if (distance === 0) { scale = 1.5; translateY = -14; }
    else if (distance === 1) { scale = 1.25; translateY = -7; }
    else if (distance === 2) { scale = 1.1; translateY = -2; }
    return { transform: `translateY(${translateY}px) scale(${scale})` };
  };

  const isItemActive = (path) => {
    if (!path) return false;
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  // Check maintenance mode on mount
  useEffect(() => {
    const checkMaintenance = async () => {
      try {
        const res = await api.get('/school-settings');
        const settings = res.data.data || {};
        if (settings.maintenanceMode === 'true' || settings.maintenanceMode === true) {
          setMaintenanceMode(true);
          setMaintenanceData({
            title: settings.maintenanceTitle || 'System Under Maintenance',
            message: settings.maintenanceMessage || 'We are performing scheduled maintenance. Please try again later.',
            endTime: settings.maintenanceEndTime || null,
          });
        }
      } catch (err) {
        // silently fail — if settings fail, don't block the app
      }
    };
    checkMaintenance();
  }, []);

  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';

  // If maintenance mode is ON and user is NOT admin/super_admin → show maintenance page
  if (maintenanceMode && !isAdmin) {
    return (
      <Box sx={{
        minHeight: '100vh',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'linear-gradient(135deg, #0f1923 0%, #1a2332 40%, #1a9e8f 100%)',
        position: 'relative', overflow: 'hidden',
      }}>
        <Box sx={{
          position: 'absolute', top: '-15%', right: '-10%',
          width: 500, height: 500, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(26,158,143,0.2) 0%, transparent 70%)',
        }} />
        <Box sx={{
          position: 'absolute', bottom: '-15%', left: '-10%',
          width: 400, height: 400, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)',
        }} />
        <Box sx={{
          textAlign: 'center', color: '#fff', maxWidth: 500, px: 3, position: 'relative', zIndex: 1,
        }}>
          <Box sx={{
            width: 80, height: 80, borderRadius: '20px',
            background: 'rgba(255,255,255,0.1)',
            backdropFilter: 'blur(10px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            mx: 'auto', mb: 3,
            border: '1px solid rgba(255,255,255,0.15)',
            fontSize: 40,
          }}>
            🔧
          </Box>
          <Typography variant="h4" fontWeight={800} sx={{ color: '#fff !important', mb: 1, letterSpacing: -0.5 }}>
            {maintenanceData.title}
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7) !important', mb: 3, lineHeight: 1.7 }}>
            {maintenanceData.message}
          </Typography>
          {maintenanceData.endTime && (
            <Box sx={{
              display: 'inline-flex', alignItems: 'center', gap: 1,
              bgcolor: 'rgba(255,255,255,0.1)', px: 2.5, py: 1, borderRadius: 2,
              border: '1px solid rgba(255,255,255,0.15)',
            }}>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6) !important' }}>
                Expected: {new Date(maintenanceData.endTime).toLocaleString('en-IN')}
              </Typography>
            </Box>
          )}
          <Box sx={{ mt: 4 }}>
            <Button variant="outlined" onClick={() => { logout(); navigate('/login'); }} sx={{
              color: '#fff !important', borderColor: 'rgba(255,255,255,0.3) !important',
              '&:hover': { borderColor: '#fff !important', bgcolor: 'rgba(255,255,255,0.1) !important' }
            }}>
              Sign Out
            </Button>
          </Box>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3) !important', mt: 4, display: 'block' }}>
            © {new Date().getFullYear()} Vidya Pratishthan's Supe English Medium School
          </Typography>
        </Box>
      </Box>
    );
  }

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Sidebar Header - Premium User Card */}
      <Box
        sx={{
          p: 2,
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          minHeight: 64,
          background: 'linear-gradient(135deg, rgba(26,158,143,0.15) 0%, transparent 100%)',
        }}
      >
        <Avatar sx={{
          width: 36, height: 36,
          background: 'linear-gradient(135deg, #1a9e8f 0%, #4db6ac 100%)',
          fontSize: 14, fontWeight: 700,
          boxShadow: '0 2px 8px rgba(26,158,143,0.3)',
        }}>
          {user?.firstName?.[0] || 'U'}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              color: '#fff',
              fontSize: 13,
              fontWeight: 700,
              lineHeight: 1.3,
            }}
            noWrap
          >
            {user?.firstName || 'User'} {user?.lastName || ''}
          </Typography>
          <Typography
            sx={{
              color: '#4db6ac',
              fontSize: 10,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: 0.8,
            }}
          >
            {user?.role === 'admin' || user?.role === 'super_admin' ? '● Principal' : `● ${user?.userRole?.name || user?.role || 'User'}`}
          </Typography>
        </Box>
        <IconButton
          onClick={handleDrawerToggle}
          sx={{
            color: 'rgba(255,255,255,0.5) !important',
            display: { sm: 'none' },
            p: 0.5,
          }}
        >
          <ChevronLeftIcon />
        </IconButton>
      </Box>

      {/* Sidebar Menu Sections */}
      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', py: 0.5 }}>
        {sidebarSections.map((section) => {
          // Filter items based on permissions
          const visibleItems = section.items.filter(item => hasPermission(item.permission));
          // Hide entire section if no items visible
          if (visibleItems.length === 0) return null;

          return (
          <Box key={section.title}>
            {/* Section Header */}
            <Box
              className="sidebar-section-header"
              onClick={() => toggleSection(section.title)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                py: 1,
                cursor: 'pointer',
                userSelect: 'none',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' }
              }}
            >
              <Typography
                sx={{
                  color: '#7b8fa0',
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                }}
              >
                {section.title}
              </Typography>
              {openSections[section.title] ?
                <ExpandLess sx={{ color: '#7b8fa0', fontSize: 16 }} /> :
                <ExpandMore sx={{ color: '#7b8fa0', fontSize: 16 }} />
              }
            </Box>

            {/* Section Items - filtered by permissions */}
            <Collapse in={openSections[section.title]} timeout="auto" unmountOnExit>
              <List disablePadding>
                {visibleItems.map((item) => (
                  <ListItem key={item.text} disablePadding>
                    <ListItemButton
                      onClick={() => navigate(item.path)}
                      selected={item.path === location.pathname}
                      sx={{
                        py: 0.5,
                        px: 2,
                        minHeight: 34,
                        '&.Mui-selected': {
                          bgcolor: 'rgba(90, 141, 238, 0.12)',
                          borderLeft: '3px solid #5a8dee',
                          '& .MuiListItemIcon-root': { color: '#5a8dee' },
                          '& .MuiListItemText-primary': { color: '#fff' },
                        },
                        '&:hover': {
                          bgcolor: 'rgba(255,255,255,0.06)',
                        }
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 28, color: '#b8c7ce' }}>
                        {React.cloneElement(item.icon, { sx: { fontSize: 17 } })}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.text}
                        primaryTypographyProps={{
                          fontSize: 12.5,
                          color: '#b8c7ce',
                          fontWeight: 400,
                          noWrap: true,
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </Box>
          );
        })}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Top Bar - White */}
      <AppBar
        position="fixed"
        sx={{
          width: { sm: navMode === 'dock' ? '100%' : `calc(100% - ${drawerWidth}px)` },
          ml: { sm: navMode === 'dock' ? 0 : `${drawerWidth}px` },
          bgcolor: 'var(--topbar-bg) !important',
          color: 'var(--topbar-text) !important',
          boxShadow: 'var(--topbar-shadow) !important',
          borderBottom: '1px solid var(--topbar-border) !important',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important',
        }}
        elevation={0}
      >
        <Toolbar sx={{ minHeight: '56px !important', px: 2 }}>
          {/* Mobile hamburger — hidden since bottom nav has Menu button */}
          <IconButton
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 1, display: 'none', color: 'var(--text-primary)' }}
          >
            <MenuIcon />
          </IconButton>

          {/* School Name with icon */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexGrow: 1 }}>
            <Box sx={{
              width: 36,
              height: 36,
              borderRadius: '10px',
              background: 'linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(var(--primary-rgb), 0.2)',
              flexShrink: 0,
            }}>
              <SchoolIcon sx={{ color: '#fff', fontSize: 20 }} />
            </Box>
            <Typography
              variant="subtitle1"
              noWrap
              sx={{
                fontWeight: 700,
                color: 'var(--text-primary) !important',
                fontSize: { xs: 13, sm: 15 },
                letterSpacing: -0.3,
              }}
            >
              Vidya Pratishthan's Supe English Medium School
            </Typography>
          </Box>

          {/* Right-side action icons */}
          <Box className="topbar-actions" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title={navMode === 'sidebar' ? "Switch to macOS Dock Mode" : "Switch to Classic Sidebar Mode"}>
              <IconButton size="small" onClick={() => setNavMode(prev => prev === 'sidebar' ? 'dock' : 'sidebar')} sx={{ color: 'var(--text-secondary)' }}>
                <TuneIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Customize Color & Theme">
              <IconButton size="small" onClick={handleThemeMenuOpen} sx={{ color: 'var(--text-secondary)' }}>
                <PaletteIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Technical Help">
              <IconButton size="small" sx={{ color: 'var(--text-secondary)' }}>
                <LiveHelpIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Notifications">
              <IconButton size="small" sx={{ color: 'var(--text-secondary)' }}>
                <Badge badgeContent={0} color="error">
                  <NotificationsIcon fontSize="small" />
                </Badge>
              </IconButton>
            </Tooltip>

            <Tooltip title="Language">
              <IconButton size="small" sx={{ color: 'var(--text-secondary)', display: { xs: 'none', md: 'flex' } }}>
                <LanguageIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Font Size">
              <IconButton size="small" sx={{ color: 'var(--text-secondary)', display: { xs: 'none', md: 'flex' } }}>
                <FormatSizeIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Search">
              <IconButton size="small" sx={{ color: 'var(--text-secondary)' }}>
                <SearchIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Profile">
              <IconButton size="small" onClick={handleMenu} sx={{ color: 'var(--text-secondary)', ml: 0.5 }}>
                <Avatar sx={{
                  width: 30, height: 30, fontSize: 13, fontWeight: 700,
                  background: 'linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%)',
                  boxShadow: '0 2px 6px rgba(var(--primary-rgb), 0.3)',
                }}>
                  {user?.firstName?.[0] || 'U'}
                </Avatar>
              </IconButton>
            </Tooltip>

            <Button
              className="logout-btn"
              variant="contained"
              size="small"
              onClick={handleLogout}
              sx={{
                ml: 1,
                background: 'linear-gradient(135deg, #e53935 0%, #c62828 100%) !important',
                color: '#fff',
                textTransform: 'none',
                fontWeight: 700,
                fontSize: 12,
                py: 0.5,
                px: 2,
                minWidth: 'auto',
                borderRadius: '8px !important',
                boxShadow: '0 2px 6px rgba(229,57,53,0.3) !important',
                transition: 'all 0.2s ease !important',
                '&:hover': {
                  background: 'linear-gradient(135deg, #c62828 0%, #b71c1c 100%) !important',
                  boxShadow: '0 4px 12px rgba(229,57,53,0.4) !important',
                  transform: 'translateY(-1px) !important',
                },
              }}
            >
              Logout
            </Button>

            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              keepMounted
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <MenuItem onClick={handleClose}>
                <ListItemIcon><AccountCircle fontSize="small" /></ListItemIcon>
                Profile
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <ListItemIcon><Logout fontSize="small" /></ListItemIcon>
                Logout
              </MenuItem>
            </Menu>

            <Menu
              id="theme-customizer-menu"
              anchorEl={themeAnchorEl}
              open={Boolean(themeAnchorEl)}
              onClose={handleThemeMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{
                sx: {
                  p: 2.5,
                  width: 250,
                  borderRadius: '16px !important',
                  boxShadow: 'var(--card-shadow) !important',
                  border: '1px solid var(--card-border) !important',
                  bgcolor: 'var(--card-bg) !important',
                  '& .MuiList-root': { p: 0 }
                }
              }}
            >
              <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1.5, color: 'var(--text-primary) !important' }}>
                Select Color Theme
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5 }}>
                {[
                  { id: 'emerald', name: 'Mystic Emerald', color: '#1a9e8f' },
                  { id: 'ocean', name: 'Ocean Breeze', color: '#2196f3' },
                  { id: 'rose', name: 'Sunset Rose', color: '#e91e63' },
                  { id: 'cyber', name: 'Cyberpunk Neon', color: '#ff007f' }
                ].map((t) => (
                  <Tooltip title={t.name} key={t.id}>
                    <Box
                      onClick={() => setActiveTheme(t.id)}
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: '50%',
                        bgcolor: t.color,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '2.5px solid',
                        borderColor: activeTheme === t.id ? 'var(--text-primary)' : 'transparent',
                        boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
                        transition: 'transform 0.2s',
                        '&:hover': { transform: 'scale(1.15)' }
                      }}
                    >
                      {activeTheme === t.id && (
                        <CheckCircleIcon sx={{ color: '#fff', fontSize: 16 }} />
                      )}
                    </Box>
                  </Tooltip>
                ))}
              </Box>
              <Divider sx={{ my: 1.5, borderColor: 'var(--card-border) !important' }} />
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="body2" fontWeight={700} sx={{ color: 'var(--text-primary) !important' }}>
                  Dark Mode
                </Typography>
                <Switch
                  checked={darkMode}
                  onChange={(e) => setDarkMode(e.target.checked)}
                  color="primary"
                  size="small"
                />
              </Box>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Sidebar Drawer */}
      <Box
        component="nav"
        sx={{ width: { sm: navMode === 'dock' ? 0 : drawerWidth }, flexShrink: { sm: 0 }, transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)' }}
      >
        {/* Mobile drawer - always available via hamburger */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              bgcolor: 'var(--sidebar-bg) !important',
              color: 'var(--sidebar-text) !important',
            },
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop drawer */}
        {navMode === 'sidebar' && (
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: 'none', sm: 'block' },
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: drawerWidth,
                bgcolor: 'var(--sidebar-bg) !important',
                borderRight: 'none !important',
                color: 'var(--sidebar-text) !important',
              },
            }}
            open
          >
            {drawer}
          </Drawer>
        )}
      </Box>

      {/* Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1.5, sm: 2 },
          width: { sm: navMode === 'dock' ? '100%' : `calc(100% - ${drawerWidth}px)` },
          bgcolor: 'var(--content-bg) !important',
          minHeight: '100vh',
          pb: { xs: 9, sm: navMode === 'dock' ? 14 : 2 },
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important',
        }}
      >
        <Toolbar sx={{ minHeight: '56px !important' }} />
        <div className="page-fade-in">
          <Outlet />
        </div>
      </Box>

      {/* macOS bottom Dock system — Desktop only */}
      {navMode === 'dock' && (
        <Box sx={{ display: { xs: 'none', sm: 'flex' } }}>
          <div className="mac-dock-container">
            <div 
              className="mac-dock"
              onMouseLeave={() => setHoveredIndex(null)}
            >
              {itemsWithIndex.map((item, idx) => {
                if (item.isDivider) {
                  return <div className="mac-dock-divider" key={item.id || idx} />;
                }

                return (
                  <div className="mac-dock-item-wrapper" key={item.id || item.text}>
                    <Tooltip title={item.text} placement="top">
                      <div
                        className="mac-dock-item"
                        style={{
                          width: 48,
                          height: 48,
                          ...getDockItemStyle(item.index),
                        }}
                        onMouseEnter={() => setHoveredIndex(item.index)}
                        onClick={item.isLink ? () => navigate(item.path) : item.onClick}
                      >
                        {React.cloneElement(item.icon, { 
                          sx: { 
                            fontSize: 24, 
                            color: (item.isLink && isItemActive(item.path)) ? 'var(--primary)' : 'var(--text-secondary)',
                            transition: 'color 0.2s',
                          } 
                        })}
                      </div>
                    </Tooltip>
                    {item.isLink && isItemActive(item.path) && (
                      <div className="mac-dock-dot" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </Box>
      )}

      {/* Mobile bottom navigation bar — shown only on xs */}
      <Box
        sx={{
          display: { xs: 'flex', sm: 'none' },
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1200,
          bgcolor: 'var(--card-bg)',
          borderTop: '1px solid var(--card-border)',
          boxShadow: '0 -2px 12px rgba(0,0,0,0.08)',
          justifyContent: 'space-around',
          alignItems: 'center',
          py: 0.5,
          px: 0.5,
        }}
      >
        {[
          { text: 'Home', icon: <DashboardIcon />, path: '/' },
          { text: 'Students', icon: <PeopleIcon />, path: '/students' },
          { text: 'Attendance', icon: <CheckCircleIcon />, path: '/attendance' },
          { text: 'Fees', icon: <AttachMoneyIcon />, path: '/fees' },
          { text: 'Menu', icon: <MenuIcon />, isMenu: true },
        ].map((item) => (
          <Box
            key={item.text}
            onClick={() => item.isMenu ? handleDrawerToggle() : navigate(item.path)}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              py: 0.5,
              px: 1,
              cursor: 'pointer',
              color: (!item.isMenu && isItemActive(item.path)) ? 'var(--primary)' : 'var(--text-muted)',
              transition: 'color 0.2s',
              '&:active': { transform: 'scale(0.92)' },
            }}
          >
            {React.cloneElement(item.icon, { sx: { fontSize: 22 } })}
            <Typography sx={{ fontSize: 9, fontWeight: 600, mt: 0.25, lineHeight: 1, color: 'inherit !important' }}>
              {item.text}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Fullscreen blurred Launchpad Overlay */}
      <div 
        className={`launchpad-overlay ${launchpadOpen ? 'open' : ''}`}
        onClick={() => setLaunchpadOpen(false)}
      >
        <div className="launchpad-search-container" onClick={(e) => e.stopPropagation()}>
          <SearchIcon className="launchpad-search-icon" />
          <input
            type="text"
            placeholder="Search all applications..."
            className="launchpad-search-input"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            autoFocus={launchpadOpen}
          />
        </div>

        <div className="launchpad-grid-container" onClick={(e) => e.stopPropagation()}>
          {sidebarSections.map((section) => {
            const filteredItems = section.items.filter(item => 
              hasPermission(item.permission) && 
              item.text.toLowerCase().includes(searchQuery.toLowerCase())
            );

            if (filteredItems.length === 0) return null;

            return (
              <div className="launchpad-category-section" key={section.title}>
                <div className="launchpad-category-title">{section.title}</div>
                <div className="launchpad-grid">
                  {filteredItems.map((item) => (
                    <div 
                      className="launchpad-item" 
                      key={item.text} 
                      onClick={() => {
                        navigate(item.path);
                        setLaunchpadOpen(false);
                        setSearchQuery('');
                      }}
                    >
                      <div className="launchpad-item-icon">
                        {React.cloneElement(item.icon, { sx: { fontSize: 24 } })}
                      </div>
                      <div className="launchpad-item-text">{item.text}</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Box>
  );
};

export default Layout;
